import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Tv, RefreshCw, StopCircle, Download, Activity, ListChecks,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading, Pager } from '../components/gtpl/GtplKit';

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

// CRM value vs GTPL value, side by side; a differing pair is shown red.
function Cell({ label, crmVal, gtplVal, mismatched }) {
  const bad = mismatched ? { color: 'var(--g-bad, var(--danger))', fontWeight: 600 } : undefined;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, minWidth: 140 }}>
      <span style={{ fontSize: '.7rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
      <span style={bad}><span style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>CRM </span>{crmVal || '—'}</span>
      <span style={bad}><span style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>GTPL </span>{gtplVal || '—'}</span>
    </span>
  );
}

// Narrower cousin of GtplVsCrmPage: for GTPL accounts that DO have a matching CRM customer,
// flags a box model/serial mismatch. Reads the box straight off the same bulk "Subscriber
// List with Active Package" scrape GtplOrphanAccountsPage already drives (confirmed live
// 2026-09-18 that report carries the box on every row), so no extra per-customer GTPL
// round-trip — unlike GTPL vs CRM's Box Model/Box Number fields, which cost a second lookup
// per customer there. Same run/stop/poll shape as its siblings.
export default function GtplStbMismatchPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canCheck = hasModuleRight('gtplSaathi', 'stbCheck');

  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loadingRows, setLoadingRows] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  const pollRef = useRef(null);

  const loadRows = useCallback(async (runId) => {
    if (!runId) { setRows([]); return; }
    setLoadingRows(true);
    try {
      const { data } = await gtplSaathiAPI.getStbCheckRows({ runId, page, limit: 50 });
      setRows(data.rows || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load rows');
    } finally {
      setLoadingRows(false);
    }
  }, [page]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getStbCheckLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load check status');
      return null;
    }
  }, []);

  useEffect(() => { loadLatest().then((r) => loadRows(r?._id)); }, []); // eslint-disable-line
  useEffect(() => { if (run?._id) loadRows(run._id); }, [page]); // eslint-disable-line

  useEffect(() => {
    const active = run?.status === 'running';
    if (!active) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows(latest?._id);
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run?.status, loadLatest, loadRows]);

  const handleRunCheck = async () => {
    setStarting(true);
    try {
      const { data } = await gtplSaathiAPI.runStbCheck();
      setRun(data.data);
      setPage(1);
      setRows([]);
      toast.success('GTPL STB mismatch check started — this runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start check');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const { data } = await gtplSaathiAPI.stopStbCheck();
      setRun(data.data);
      toast.success('Stop requested — finishing the page currently in progress, then it\'ll halt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const handleFix = async (row) => {
    setFixingId(row._id);
    try {
      await gtplSaathiAPI.fixStbCheckInCrm(row._id);
      toast.success('CRM updated from GTPL');
      loadRows(run?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setFixingId(null);
    }
  };

  const handleFixAll = async () => {
    if (!run?._id) return;
    if (!window.confirm('This copies GTPL\'s box model / box number into the CRM record for every mismatched account. Continue?')) return;
    setFixingAll(true);
    try {
      const { data } = await gtplSaathiAPI.fixAllStbCheckInCrm(run._id);
      if (data.data?.total) toast.success(`Updated ${data.data.fixed} of ${data.data.total} customer(s) in the CRM${data.data.failed ? `, ${data.data.failed} failed` : ''}`);
      else toast('Nothing to update — no unresolved box mismatches', { icon: 'ℹ️' });
      loadRows(run._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setFixingAll(false);
    }
  };

  const isRunning = run?.status === 'running';
  const runTone = isRunning ? 'info' : run?.status === 'failed' ? 'bad' : run?.status === 'cancelled' ? 'mute' : 'ok';
  const runLabel = isRunning ? (run.cancelRequested ? 'Stopping…' : 'Running') : run?.status === 'failed' ? 'Failed' : run?.status === 'cancelled' ? 'Stopped' : 'Completed';

  return (
    <Screen>
      <PageHead icon={Tv} tone="ok" title="GTPL STB Mismatch"
        sub={<>For every GTPL Saathi account that already matches a CRM customer, compares the box model/serial on file — read straight off GTPL's bulk "Subscriber List with Active Package" report, same scrape <Link to="/gtpl-not-in-crm">GTPL Not In CRM</Link> uses, so no extra per-customer lookup. Accounts with no CRM match at all show up there instead, not here. Runs in the background even if you navigate away.</>}>
        {isRunning && (
          <button type="button" className="btn btn-sm btn-danger" disabled={stopping || run?.cancelRequested || !canCheck} onClick={handleStop}>
            <StopCircle size={14} /> {run?.cancelRequested || stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={starting || isRunning || !canCheck} onClick={handleRunCheck}>
          <RefreshCw size={14} style={starting || isRunning ? { animation: 'spin 1s linear infinite' } : undefined} />
          {isRunning ? 'Checking…' : starting ? 'Starting…' : 'Run Check'}
        </button>
      </PageHead>

      {!canCheck && <Notice tone="bad">You don't have permission to run this check.</Notice>}

      <Panel icon={Activity} tone={run ? runTone : 'mute'} title={run ? `Latest check: ${runLabel}` : 'Latest check'}
        sub={run ? `${isRunning ? 'Started' : 'Completed'} ${fmtDateTime(isRunning ? run.startedAt : run.completedAt)}` : undefined}>
        {!run ? (
          <Empty icon={Tv} title="No check has been run yet" />
        ) : (
          <>
            <Stats>
              <Stat tone="info" value={run.totalGtplActive || 0} label="GTPL active accounts scraped" />
              <Stat tone={run.mismatchCount ? 'bad' : 'ok'} value={run.mismatchCount || 0} label="Box mismatches" />
            </Stats>
            {run.failMessage && (
              <div style={{ marginTop: 12 }}><Notice tone={run.status === 'cancelled' ? 'mute' : 'bad'}>{run.failMessage}</Notice></div>
            )}
          </>
        )}
      </Panel>

      {run && (
        <Panel icon={ListChecks} tone={pagination.total ? 'bad' : 'mute'} title="Box mismatches" count={pagination.total}
          right={<>
            {run.mismatchCount > 0 && (
              <button type="button" className="btn btn-sm btn-secondary" disabled={fixingAll || !canCheck} onClick={handleFixAll}>
                <Download size={13} /> {fixingAll ? 'Updating…' : 'Fix all in CRM'}
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loadingRows} onClick={() => loadRows(run._id)}>
              <RefreshCw size={13} style={loadingRows ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
            </button>
          </>}>
          {loadingRows ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty tone={isRunning ? undefined : 'ok'}
              title={isRunning ? 'Results will appear here as the check progresses…' : 'None found — every matched account\'s box agrees with GTPL.'} />
          ) : (
            <div className="gx-list">
              {rows.map((r) => {
                const diff = new Set(r.diffFields || []);
                const fixing = fixingId === r._id;
                const fixed = r.crmFix?.status === 'done';
                return (
                  <Item key={r._id} tone={fixed ? 'ok' : 'bad'}
                    title={<Link to={`/customers/${r.customer}`}>{r.name}</Link>}
                    sub={<>Bill No. {r.billingNumber || '—'} · <span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span></>}
                    right={fixed ? <Pill tone="ok" dot>{r.crmFix.message || 'Updated'}</Pill> : null}
                    meta={<>
                      <Cell label="Box model" crmVal={r.crmStbModel} gtplVal={r.gtplStbModel} mismatched={diff.has('stbModel')} />
                      <Cell label="Box number" crmVal={r.crmStbSerial} gtplVal={r.gtplStbSerial} mismatched={diff.has('stbSerial')} />
                      {r.crmFix?.status === 'failed' && <Pill tone="bad">{r.crmFix.message}</Pill>}
                    </>}
                    actions={fixed ? null : (
                      <button type="button" className="btn btn-sm btn-primary" disabled={fixing || !canCheck} onClick={() => handleFix(r)}>
                        <Download size={12} style={fixing ? { animation: 'spin 1s linear infinite' } : undefined} /> {fixing ? 'Updating…' : 'Fix in CRM'}
                      </button>
                    )} />
                );
              })}
            </div>
          )}

          <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
        </Panel>
      )}
    </Screen>
  );
}
